// Tenantra Inc. - Phone Verification Page (Step 1)
import { useState, useEffect } from 'react';
import { 
  Phone, CheckCircle, Loader2, RefreshCw, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { navigate } from '@/services/navigation';
import { TableSkeleton } from '@/components/dashboard/TableKit';

export default function PhoneVerificationPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phoneOtp, setPhoneOtp] = useState('');
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [sendingPhoneOtp, setSendingPhoneOtp] = useState(false);
  const [verifyingPhone, setVerifyingPhone] = useState(false);
  const [smsStatus, setSmsStatus] = useState('');

  useEffect(() => { fetchUserData(); }, []);

  const fetchUserData = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      if (response.data?.phone_verified) {
        navigate('/landlord/verify');
      }
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setLoading(false);
    }
  };

  const sendPhoneOtp = async () => {
    try {
      setSendingPhoneOtp(true);
      const response = await api.post('/verification/phone/send', {
        identifier: user.phone,
        type: 'phone_verification'
      });
      setPhoneOtpSent(true);
      setSmsStatus(response.data.sms_status || '');
      toast.success('Verification code sent to your phone');
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setSendingPhoneOtp(false);
    }
  };

  const verifyPhoneOtp = async () => {
    try {
      setVerifyingPhone(true);
      const response = await api.post('/verification/phone/verify', {
        identifier: user.phone,
        code: phoneOtp,
        type: 'phone_verification'
      });
      if (response.data.valid) {
        toast.success('Phone verified!');
        setTimeout(() => { navigate('/landlord/verify'); }, 1500);
      } else {
        toast.error(response.data.message || 'Invalid code');
      }
    } catch (err) {
      toast.error(apiHelpers.handleError(err));
    } finally {
      setVerifyingPhone(false);
    }
  };

  if (loading) {
    return <div data-testid="phone-verify-loading"><TableSkeleton rows={4} spans={[6, 3, 3]} /></div>;
  }

  if (user?.phone_verified) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="text-center">
          <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
          <p className="text-sm text-slate-600">Phone already verified. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto pt-6 sm:pt-10" data-testid="phone-verify-page">
      {/* Progress */}
      <div className="flex items-center gap-1.5 mb-5">
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-teal-600 text-white font-bold text-xs">1</div>
        <div className="flex-1 h-0.5 rounded-full bg-slate-200" />
        <div className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-200 text-slate-400 font-bold text-xs">2</div>
      </div>

      {/* Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 sm:p-6 shadow-sm">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-slate-800 mb-1">Verify your phone</h2>
          <p className="text-sm text-slate-500">
            {phoneOtpSent 
              ? 'Enter the code sent to your phone' 
              : 'We\'ll send a 6-digit code via SMS'}
          </p>
        </div>

        {!phoneOtpSent ? (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-6 h-6 text-teal-600" />
              </div>
              <p className="text-sm text-slate-500">
                Code will be sent to<br />
                <strong className="text-slate-700">{user?.phone || 'No phone set'}</strong>
              </p>
            </div>
            <Button
              onClick={sendPhoneOtp}
              disabled={sendingPhoneOtp || !user?.phone}
              className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm border-0"
              data-testid="send-phone-otp-btn"
            >
              {sendingPhoneOtp ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </span>
              ) : (
                'Send Code'
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-3">
                <Phone className="w-6 h-6 text-teal-600" />
              </div>
              <p className="text-sm text-slate-500">
                We've sent a 6-digit code to<br />
                <strong className="text-slate-700">{user?.phone}</strong>
              </p>
            </div>

            {smsStatus && (
              <div className={`p-2 rounded-lg text-xs text-center ${smsStatus.includes('Vonage') ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-amber-50 text-amber-600 border border-amber-200'}`}>
                {smsStatus}
              </div>
            )}

            <div>
              <Input
                placeholder="000000"
                value={phoneOtp}
                onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-xl font-mono tracking-widest h-11 bg-white border-slate-200 text-slate-800"
                maxLength={6}
                data-testid="phone-otp-input"
              />
            </div>

            <Button
              onClick={verifyPhoneOtp}
              disabled={phoneOtp.length < 6 || verifyingPhone}
              className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm border-0"
              data-testid="verify-phone-btn"
            >
              {verifyingPhone ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Verifying...
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  Verify & Continue <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </span>
              )}
            </Button>

            <div className="text-center">
              <button
                onClick={sendPhoneOtp}
                disabled={sendingPhoneOtp}
                className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1.5 mx-auto"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${sendingPhoneOtp ? 'animate-spin' : ''}`} />
                {sendingPhoneOtp ? 'Sending...' : "Didn't receive a code? Resend"}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Compact footer note */}
      <p className="text-xs text-slate-400 text-center mt-4">
        Phone verification helps secure your account and enables SMS notifications.
      </p>
    </div>
  );
}
